import { useSyncExternalStore } from 'react'
import { readStorage, writeStorage } from './utils'

/** Minimal external store; enough for a handful of app-wide preferences. */
export function createStore<T>(initial: T, persistKey?: string) {
  let state: T = persistKey ? readStorage(persistKey, initial) : initial
  const listeners = new Set<() => void>()

  const store = {
    get: () => state,
    set(next: T | ((prev: T) => T)) {
      state = typeof next === 'function' ? (next as (prev: T) => T)(state) : next
      if (persistKey) writeStorage(persistKey, state)
      listeners.forEach((l) => l())
    },
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
  return store
}

export function useStore<T>(store: ReturnType<typeof createStore<T>>): T {
  return useSyncExternalStore(store.subscribe, store.get, store.get)
}

export type Density = 'comfortable' | 'compact'

interface Preferences {
  /** The user's wish; guests are always shown safe content regardless. */
  safeMode: boolean
  density: Density
  recentSearches: string[]
}

export const preferences = createStore<Preferences>(
  { safeMode: true, density: 'comfortable', recentSearches: [] },
  'kagami:prefs',
)

export function rememberSearch(query: string) {
  const q = query.trim()
  if (!q) return
  preferences.set((p) => ({
    ...p,
    recentSearches: [q, ...p.recentSearches.filter((s) => s !== q)].slice(0, 8),
  }))
}

/**
 * The ordered ids of whatever gallery is on screen, so the image viewer can
 * step to the previous / next image and pull in the next page at the end.
 */
interface GalleryState {
  ids: string[]
  loadMore: (() => void) | null
  hasMore: boolean
}

export const gallery = createStore<GalleryState>({ ids: [], loadMore: null, hasMore: false })
